import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef } from 'react'
import { fontSans, fontMono, hexToRgba } from './theme'

const ACCENT = '#9D4EDD'

function BuilderVisual() {
  return (
    <div
      style={{
        background: 'linear-gradient(160deg, rgba(157,78,221,0.06), rgba(10,15,31,0.4))',
        border: `1px solid ${hexToRgba(ACCENT, 0.25)}`,
        borderRadius: '20px',
        padding: '1.75rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* corner glow */}
      <div
        style={{
          position: 'absolute',
          top: '-60px',
          right: '-60px',
          width: '220px',
          height: '220px',
          background: `radial-gradient(circle, ${hexToRgba(ACCENT, 0.18)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* 1 — plain English in */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <div
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontFamily: fontSans,
            fontSize: '0.7rem',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          You
        </div>
        <div
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '4px 14px 14px 14px',
            padding: '12px 16px',
            fontFamily: fontSans,
            fontSize: '0.82rem',
            lineHeight: 1.6,
            color: 'rgba(255,255,255,0.8)',
          }}
        >
          “An <b style={{ color: '#fff' }}>active customer</b> is anyone with a
          subscription that hasn't expired. Customers are{' '}
          <code style={monoCode}>crm.accounts</code> joined to{' '}
          <code style={monoCode}>billing.subscriptions</code> on{' '}
          <code style={monoCode}>account_id</code>. Emails and phones are PII —
          never show them raw.”
        </div>
      </div>

      {/* connector */}
      <FlowArrow label="parses · binds to real columns · asks you to confirm" />

      {/* 2 — governed ontology out */}
      <div
        style={{
          background: 'rgba(10,15,31,0.6)',
          border: `1px solid ${hexToRgba(ACCENT, 0.35)}`,
          borderRadius: '14px',
          padding: '14px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '10px',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: fontMono,
              fontSize: '0.72rem',
              fontWeight: 600,
              color: ACCENT,
              letterSpacing: '0.04em',
            }}
          >
            ENTITY
          </span>
          <span
            style={{
              fontFamily: fontSans,
              fontSize: '0.95rem',
              fontWeight: 700,
              color: '#fff',
            }}
          >
            Customer
          </span>
          <span
            style={{
              fontFamily: fontMono,
              fontSize: '0.68rem',
              color: 'rgba(255,255,255,0.45)',
            }}
          >
            crm.accounts ⋈ billing.subscriptions · on account_id
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '8px',
          }}
        >
          <FieldChip name="account_name" meta="text" />
          <FieldChip
            name="primary_contact_email"
            meta="🔒 hash-masked"
            locked
          />
          <FieldChip name="is_active" meta="derived rule" derived />
          <FieldChip name="phone" meta="⛔ blocked" blocked />
        </div>
        <div
          style={{
            marginTop: '10px',
            fontFamily: fontMono,
            fontSize: '0.68rem',
            color: 'rgba(255,255,255,0.4)',
          }}
        >
          rule · is_active := subscription.status = &apos;active&apos; AND
          ends_at &gt; now()
        </div>
      </div>

      {/* connector */}
      <FlowArrow label="any AI agent asks over MCP · policy applies at read time" />

      {/* 3 — governed answer */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '9px',
            background: hexToRgba(ACCENT, 0.15),
            border: `1px solid ${hexToRgba(ACCENT, 0.4)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <rect
              x="4"
              y="7"
              width="16"
              height="12"
              rx="3"
              stroke={ACCENT}
              strokeWidth="1.6"
            />
            <path
              d="M12 4v3M9 13h.01M15 13h.01"
              stroke={ACCENT}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div style={{ flexGrow: 1 }}>
          <div
            style={{
              fontFamily: fontMono,
              fontSize: '0.72rem',
              color: 'rgba(255,255,255,0.5)',
              marginBottom: '6px',
            }}
          >
            agent → Customer? select account_name, email, is_active
          </div>
          <div
            style={{
              background: 'rgba(45,212,191,0.05)',
              border: '1px solid rgba(45,212,191,0.25)',
              borderRadius: '4px 14px 14px 14px',
              padding: '10px 14px',
              fontFamily: fontMono,
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.85)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <span>
              Acme Ltd · <span style={{ color: '#2DD4BF' }}>a3f9…c2</span> ·{' '}
              <span style={{ color: '#2DD4BF' }}>true</span>
            </span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '0.65rem',
                color: 'rgba(45,212,191,0.8)',
                border: '1px solid rgba(45,212,191,0.3)',
                borderRadius: '100px',
                padding: '2px 9px',
                background: 'rgba(45,212,191,0.06)',
              }}
            >
              audit ✓ logged
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const monoCode: React.CSSProperties = {
  fontFamily: fontMono,
  fontSize: '0.76rem',
  background: 'rgba(157,78,221,0.12)',
  color: '#c4b5fd',
  borderRadius: '5px',
  padding: '1px 5px',
}

function FieldChip({
  name,
  meta,
  locked,
  derived,
  blocked,
}: {
  name: string
  meta: string
  locked?: boolean
  derived?: boolean
  blocked?: boolean
}) {
  const tone = locked
    ? '#F5B850'
    : derived
      ? '#4A90E2'
      : blocked
        ? '#F87171'
        : 'rgba(255,255,255,0.5)'
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: '9px',
        padding: '8px 11px',
      }}
    >
      <div
        style={{
          fontFamily: fontMono,
          fontSize: '0.72rem',
          color: 'rgba(255,255,255,0.85)',
        }}
      >
        {name}
      </div>
      <div style={{ fontFamily: fontSans, fontSize: '0.68rem', color: tone }}>
        {meta}
      </div>
    </div>
  )
}

function FlowArrow({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        margin: '12px 0 12px 42px',
      }}
    >
      <svg width="14" height="26" viewBox="0 0 14 26" fill="none">
        <path
          d="M7 0v22"
          stroke={hexToRgba(ACCENT, 0.5)}
          strokeWidth="1.4"
          strokeDasharray="3 3"
          className="ontext-flow"
        />
        <path
          d="M2.5 19 7 25l4.5-6"
          stroke={hexToRgba(ACCENT, 0.7)}
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      <span
        style={{
          fontFamily: fontSans,
          fontSize: '0.72rem',
          color: 'rgba(255,255,255,0.45)',
        }}
      >
        {label}
      </span>
    </div>
  )
}

const featureRows = [
  {
    title: 'Build ontologies by conversation',
    body: 'A conversational builder turns plain-English definitions into proposed entities, joins, rules and PII policies — every field bound to a real schema.table.column, nothing committed until you confirm.',
  },
  {
    title: 'Serve any agent over MCP & REST',
    body: 'One command generates a governed MCP server. Claude, Cursor or your own LangGraph stack queries Customer — never raw tables — and gets consistent, rule-computed answers.',
  },
  {
    title: 'Policy at agent-read time',
    body: 'Masking, trust tiers and residency rules are enforced when the agent reads, not documented in a wiki. PII leaves hashed or not at all.',
  },
  {
    title: 'A receipt for every question',
    body: 'The ontology is versioned like code — commits, diffs, authors — and every agent query writes an audit event. That artifact is what unblocks legal review.',
  },
]

const metaChips = [
  'Open Source · Apache-2.0',
  'Self-host in one Docker command',
  'Start from CRM & ecommerce templates',
  'Import / export OWL · RDF · Turtle',
  'Entity graph view',
]

export function Ontext() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      id="ontext"
      style={{
        padding: '7rem 2rem',
        background:
          'linear-gradient(180deg, #0A0F1F 0%, #0e1128 50%, #0A0F1F 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${hexToRgba(ACCENT, 0.4)}, transparent)`,
        }}
      />
      {/* ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '-200px',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${hexToRgba(ACCENT, 0.07)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '4rem',
            alignItems: 'center',
          }}
        >
          {/* Narrative side */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span
              style={{
                fontFamily: fontSans,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: ACCENT,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '1rem',
                padding: '5px 14px',
                borderRadius: '100px',
                background: hexToRgba(ACCENT, 0.1),
                border: `1px solid ${hexToRgba(ACCENT, 0.3)}`,
              }}
            >
              Ontext · Open Source
            </span>
            <h2
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(1.9rem, 3.6vw, 2.9rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.12,
                marginBottom: '1.25rem',
              }}
            >
              Your AI agents deserve{' '}
              <span
                style={{
                  background: `linear-gradient(135deg, #b794f4, ${ACCENT})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                trusted answers.
              </span>
            </h2>
            <p
              style={{
                fontFamily: fontSans,
                fontSize: '1.02rem',
                color: 'rgba(255,255,255,0.55)',
                lineHeight: 1.75,
                marginBottom: '2rem',
              }}
            >
              Text-to-SQL fails because agents don&apos;t know what an{' '}
              <i>active customer</i> is — and legal blocks agents because nobody
              can guarantee PII stays put. Ontext is the translator and the
              bouncer between AI agents and your database:{' '}
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                agents get correct answers, PII never leaks, every question has
                a receipt.
              </span>
            </p>

            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}
            >
              {featureRows.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                  style={{ display: 'flex', gap: '14px' }}
                >
                  <div
                    style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '8px',
                      background: hexToRgba(ACCENT, 0.12),
                      border: `1px solid ${hexToRgba(ACCENT, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '2px',
                      color: ACCENT,
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M4 8.2 6.5 10.7 12 5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: fontSans,
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.92)',
                        marginBottom: '3px',
                      }}
                    >
                      {f.title}
                    </div>
                    <div
                      style={{
                        fontFamily: fontSans,
                        fontSize: '0.85rem',
                        color: 'rgba(255,255,255,0.5)',
                        lineHeight: 1.65,
                      }}
                    >
                      {f.body}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* meta chips */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginTop: '2rem',
              }}
            >
              {metaChips.map((c) => (
                <span
                  key={c}
                  style={{
                    fontFamily: fontSans,
                    fontSize: '0.74rem',
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.6)',
                    padding: '5px 12px',
                    borderRadius: '100px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.09)',
                  }}
                >
                  {c}
                </span>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '2.25rem',
                flexWrap: 'wrap',
              }}
            >
              <a
                href="#demo"
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  textDecoration: 'none',
                  padding: '0.75rem 1.6rem',
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, #7C3AED, ${ACCENT})`,
                  boxShadow: `0 0 30px ${hexToRgba(ACCENT, 0.35)}`,
                  transition: 'all 0.25s ease',
                  display: 'inline-block',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = `0 8px 36px ${hexToRgba(ACCENT, 0.5)}`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = `0 0 30px ${hexToRgba(ACCENT, 0.35)}`
                }}
              >
                See Ontext in Action
              </a>
            </div>
          </motion.div>

          {/* Visual side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{
              duration: 0.7,
              delay: 0.2,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <BuilderVisual />
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes ontextFlow {
          to { stroke-dashoffset: -12; }
        }
        .ontext-flow {
          animation: ontextFlow 1.2s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .ontext-flow { animation: none; }
        }
      `}</style>
    </section>
  )
}
